import { createElement } from 'react';
import { createRoot, type Root, type RootOptions } from 'react-dom/client';

import { ApplicationError } from '@/components/ApplicationError';

type RootContainer = Element & { _inertiaReactRoot?: Root };

/**
 * Returns the React root for an Inertia container, creating it only once and reusing it
 * on subsequent calls.
 *
 * In dev a stale module graph (HMR, or a Vite dependency re-optimization "reloading"
 * race on the first visit to a route whose deps aren't pre-bundled) can execute an app
 * entry's `boot()` twice against the same `#app` element. Without this guard the second
 * `createRoot()` lands on a container that already has a root, which orphans the first
 * root's lazy `<Suspense>` fallback DOM and throws
 * `NotFoundError: Failed to execute 'removeChild' on 'Node'`, breaking the page.
 * Reusing the existing root makes mounting idempotent, as React itself recommends.
 */
export function getInertiaRoot(el: Element, options?: RootOptions): Root {
  const container = el as RootContainer;

  if (!container._inertiaReactRoot) {
    const root = createRoot(el, {
      ...options,
      onUncaughtError(error, errorInfo) {
        root.render(createElement(ApplicationError));
        options?.onUncaughtError?.(error, errorInfo);
      }
    });
    container._inertiaReactRoot = root;
  }

  return container._inertiaReactRoot;
}
